import { Sello } from './Sello';
import { NEGOCIO } from '@/lib/negocio';
import { NavLink } from 'react-router-dom';
import { iconoDe, NOMBRE_DE_GRUPO } from './iconos-del-menu';
import { useUiStore } from '@/store/ui.store';
import { useMenu } from '@/hooks/useMenu';
import type { MenuResponse } from '@/api/modulos.api';
import { cn } from '@/lib/cn';

/**
 * FASE 4 (El Edificio) — EL SIDEBAR COME DEL REGISTRO DE MÓDULOS.
 *
 * Antes tenía una lista de 19 items cableada aquí adentro, con banderas
 * `admin` y `owner` para esconder links. Eso hacía dos cosas mal:
 *
 * 1. **Dejaba la Fase 3 sin efecto.** El servidor decidía qué módulos tiene
 *    cada inquilino y este componente le mostraba los mismos 19 links a todos.
 *    Un cliente que no compró Trabajos veía «Trabajos» igual.
 * 2. **Escondía botones en vez de no tenerlos.** Un link oculto sigue estando
 *    en el bundle que descarga cualquiera; el prompt lo dice sin rodeos:
 *    esconder un botón no es seguridad.
 *
 * Ahora la lista viene de `GET /api/modulos/menu`. El servidor ya aplicó el rol
 * EFECTIVO dentro de ese negocio (que puede no ser el rol global del usuario) y
 * los módulos activos del inquilino. **Aquí no se filtra nada: solo se dibuja.**
 */

/**
 * La VISTA, sin hooks: recibe todo por props. Se separa asi para poder probarla
 * con `renderToStaticMarkup`, que es la convencion del repo (ver
 * `BotControlToggleView`). El contenedor de abajo es el que habla con el
 * servidor.
 */
export function SidebarView({
  sidebarOpen,
  datos,
  cargando,
  fallo,
  sinNegocio = false,
}: {
  sidebarOpen: boolean;
  datos: MenuResponse | undefined;
  cargando: boolean;
  fallo: boolean;
  /** Ya se sabe qué negocios hay y no hay ninguno activo. */
  sinNegocio?: boolean;
}) {
  return (
    <aside
      className={cn(
        // 21-oct, macOS 27: barra lateral opaca de 240 pegada al borde (#E6E6E6 / #161616), sin radio ni filo.
        'flex shrink-0 flex-col overflow-hidden bg-[var(--mac-lateral)] text-slate-900 transition-[width] duration-200 dark:text-slate-100',
        sidebarOpen ? 'w-60' : 'w-0 overflow-hidden md:w-14',
      )}
    >
      <div
        data-tour="logo"
        className="flex h-14 shrink-0 items-center gap-2.5 px-[18px] lateral:h-[52px]"
      >
        {/* 13-oct, Kervens: arriba va el nombre del negocio, con su sello; el sistema no se nombra. */}
        <Sello nombre={datos?.negocio ?? NEGOCIO} />
        {sidebarOpen && (
          <span className="min-w-0 truncate text-sm font-semibold text-slate-900 dark:text-slate-50">
            {datos?.negocio ?? NEGOCIO}
          </span>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto overscroll-contain px-2.5 pb-5 pt-1">
        {cargando && (
          // Sin esto, al cambiar de negocio el sidebar parpadea vacío y parece
          // que se rompió.
          <p className="px-3 py-2 text-xs text-slate-400">Cargando el menú…</p>
        )}

        {sinNegocio && (
          // ESTE es el estado que antes se veía como "Cargando el menú…" para
          // siempre. Que diga qué pasa importa: un spinner eterno hace pensar
          // que el sistema es lento, y esto no se arregla esperando.
          <p className="px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
            No hay un negocio activo. Elige uno arriba; si el selector está
            vacío, tu usuario todavía no tiene acceso a ninguno.
          </p>
        )}

        {fallo && (
          // Se dice qué pasó y qué hacer. Un sidebar vacío sin explicación se
          // lee como «el sistema se rompió».
          <p className="px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
            No se pudo cargar el menú. Recarga la página; si sigue igual, revisa
            que la API esté arriba.
          </p>
        )}

        {datos?.menu.map((grupo) => (
          <div key={grupo.grupo}>
            {sidebarOpen && (
              // Rótulo de sección: 11 seminegrita, gris, 20 de aire arriba y 6 abajo (sin mayúsculas).
              <p className="mb-1.5 ml-[9px] mt-5 text-[11px] font-semibold leading-[14px] text-[var(--mac-lateral-rotulo)]">
                {NOMBRE_DE_GRUPO[grupo.grupo] ?? grupo.grupo}
              </p>
            )}
            <div>
              {grupo.items.map((item) => {
                const Icono = iconoDe(item.icono);
                return (
                  <NavLink
                    key={item.ruta}
                    to={item.ruta}
                    end={item.ruta === '/'}
                    data-tour={item.ruta}
                    className={({ isActive }) =>
                      cn(
                        // Fila de 32 (44 en el teléfono), radio 8; lo elegido en gris, el ícono siempre en el acento.
                        'flex min-h-11 items-center gap-2 rounded-[8px] pl-[9px] pr-2 text-sm text-slate-900 dark:text-slate-100 lateral:min-h-8',
                        isActive && 'bg-[var(--mac-lateral-sel)]',
                      )
                    }
                  >
                    <Icono className="h-[17px] w-[17px] shrink-0 text-acento-icono" strokeWidth={1.75} />
                    {sidebarOpen && <span className="truncate">{item.etiqueta}</span>}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  );
}

/** Contenedor: pide el menu del negocio activo y se lo pasa a la vista. */
export function Sidebar() {
  const sidebarOpen = useUiStore((s) => s.sidebarOpen);
  const { datos, cargando, fallo, sinNegocio } = useMenu();
  return (
    <SidebarView
      sidebarOpen={sidebarOpen}
      datos={datos}
      cargando={cargando}
      fallo={fallo}
      sinNegocio={sinNegocio}
    />
  );
}
